import { Alert } from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useMemo } from 'react';

import type { AllImpactGroupsQuery } from '../../graphql/generated/queries.gql.generated';
import { useAllImpactGroupsQuery } from '../../graphql/generated/queries.gql.generated';
import { useImpactGroupsTableColumns } from './columns';

type ImpactGroupRow = NonNullable<
  NonNullable<AllImpactGroupsQuery['impactGroupTemplates']>['groups'][number]
>;

const AdminImpactGroupTemplateTable = () => {
  const { data, loading, error } = useAllImpactGroupsQuery();

  const columns = useImpactGroupsTableColumns();

  // Memoize filtered data to prevent unnecessary re-renders
  const tableData = useMemo(
    () =>
      data?.impactGroupTemplates?.groups?.filter(
        (group): group is ImpactGroupRow => group !== null,
      ) ?? [],
    [data?.impactGroupTemplates?.groups],
  );

  const table = useMaterialReactTable<ImpactGroupRow>({
    columns,
    data: tableData,
    // Critical: Provide stable row IDs to prevent React reconciliation issues
    getRowId: (row) => String(row.rowId),
    state: {
      isLoading: loading,
      showProgressBars: loading,
      density: 'compact',
    },
    initialState: {
      density: 'compact',
      columnVisibility: {
        rowId: false,
        label: false,
        description: true,
        actionUrl: false,
        createdAt: false,
        updatedAt: false,
        updatedBy: false,
        templateExpiresAt: false,
        active: true,
      },
    },
    renderFallbackValue: '--',
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnFilters: false,
    manualFiltering: true,
    // TODO: Add row click handler with CRUD operations dialog
    // muiTableBodyRowProps: ({ row }) => ({})

    muiTableBodyCellProps: { sx: { fontSize: '0.875rem' } },
    muiTableHeadCellProps: { sx: { fontSize: '0.75rem', textTransform: 'uppercase' } },
  });

  if (error) {
    return <Alert severity="error">Failed to load impact groups: {error.message}</Alert>;
  }

  return (
    <>
      <MaterialReactTable table={table} />
      {/* TODO: Add CRUD operations dialog for Create/Edit/Delete actions */}
    </>
  );
};

export default AdminImpactGroupTemplateTable;
